import {
    Box,
    Button,
    Flex,
    HStack,
    Icon as ChakraIcon,
    IconButton,
    Tag,
    Tooltip,
} from '@chakra-ui/react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import { TbBuilding } from 'react-icons/tb';
import RefreshButton from '../../../components/RefreshButton';
import LocationSelector from '../../../components/LocationSelector';
import CollapsibleSearch, { LIST_PAGE_SEARCH_WIDTH } from '../../../components/CollapsibleSearch';
import TableSettingsButton from '../../../components/TableSettingsButton';
import InvoiceAdvancedFilters from '../../../components/invoice/InvoiceAdvancedFilters';
import { INVOICES_LIST_TAB_ICONS, INVOICES_ACTIVITY_BUTTON_ICON } from './useInvoicesListPage';
import { InvoicesListTable } from './InvoicesListTable';
import AllInvoiceActivityLogDrawer from '../../../modals/invoice/AllInvoiceActivityLogDrawer';
import { InvoicesListModals } from './InvoicesListModals';
import type { InvoicesListPageModel } from './useInvoicesListPage';
import usePageTitle from '../../../hooks/usePageTitle';
import ListTabRow from '../../../components/ListTabRow';
import { Badge, Icon } from '../../../ui';
import { NegotiationFeedView } from '../../../features/invoicing/negotiation/NegotiationFeedView';
import { INVOICES_LOCATION_IDS_STORAGE_KEY } from '../../../shared/invoicesListViewState';
import { useNegotiationUnreadCount } from '../../../hooks/invoice/useInvoiceNegotiation';

export function InvoicesListView({ page }: { page: InvoicesListPageModel }) {
    usePageTitle('Parts Invoicing');
    const {
        isInvoicingDemo,
        type,
        showArchived,
        showNegotiation,
        selectedNegotiationId,
        searchQuery,
        setSearchQuery,
        locationIds,
        additionalFilters,
        handleFiltersChange,
        tableSettings,
        updateTableSettings,
        colWidths,
        resetColWidths,
        handleRefresh,
        isRefetching,
        onCreateModalOpen,
        onPartsInvoicingGuideOpen,
        onDemoResetOpen,
        resetInvoicingDemoMutation,
        handleTypeChange,
        handleToggleArchived,
        handleToggleNegotiation,
        handleSelectNegotiation,
        isActivityLogOpen,
        onActivityLogOpen,
        onActivityLogClose,
        toolbarRef,
        locationCompact,
        searchCompact,
        handleLocationChange,
        nexReconciliationIdentifierMode,
    } = page;

    const { data: unreadData } = useNegotiationUnreadCount(true);
    const unreadCount = unreadData?.count ?? 0;

    const tabButtonSx = (isActive: boolean) => ({
        size: 'sm' as const,
        h: '32px',
        px: 4,
        borderRadius: 'full',
        fontWeight: 'medium',
        fontSize: 'sm',
        flexShrink: 0,
        bg: isActive ? 'brand.solid' : 'transparent',
        color: isActive ? 'fg.onBrand' : 'fg.muted',
        _hover: {
            bg: isActive ? 'brand.solid' : 'bg.subtle',
        },
        _active: {
            bg: isActive ? 'brand.solid' : 'bg.subtle',
        },
    });

    return (
        <Box
            w="calc(100% + 2rem)"
            mx={-4}
            mt={-4}
            mb={-2}
            pb={0}
            h="calc(100dvh - var(--partbay-topbar))"
            display="flex"
            flexDirection="column"
            overflow="hidden"
        >
            <ListTabRow
                actions={
                    !showNegotiation ? (
                        <HStack spacing={2} flexShrink={0}>
                            {isInvoicingDemo && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    colorScheme="orange"
                                    onClick={onDemoResetOpen}
                                    isLoading={resetInvoicingDemoMutation.isPending}
                                >
                                    Reset
                                </Button>
                            )}
                            <Button
                                size="sm"
                                bg="#0071E3"
                                color="white"
                                _hover={{ bg: '#3544a0' }}
                                onClick={onCreateModalOpen}
                            >
                                Create
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="gray"
                                color="gray.600"
                                leftIcon={<ChakraIcon as={INVOICES_ACTIVITY_BUTTON_ICON} boxSize={3.5} />}
                                onClick={onActivityLogOpen}
                            >
                                Activity
                            </Button>
                            <TableSettingsButton
                                settings={tableSettings}
                                onUpdateSettings={updateTableSettings}
                                hasCustomColumnWidths={Object.keys(colWidths).length > 0}
                                onResetColumnWidths={resetColWidths}
                            />
                            <RefreshButton refetch={handleRefresh} isLoading={isRefetching} />
                        </HStack>
                    ) : (
                        <HStack spacing={2} flexShrink={0}>
                            <Button
                                size="sm"
                                variant="ghost"
                                colorScheme="gray"
                                color="gray.600"
                                leftIcon={<ChakraIcon as={INVOICES_ACTIVITY_BUTTON_ICON} boxSize={3.5} />}
                                onClick={onActivityLogOpen}
                            >
                                Activity
                            </Button>
                        </HStack>
                    )
                }
            >
                    {(['all', 'flagged', 'submitted'] as const).map((el) => {
                        const isActive = !showNegotiation && !showArchived && type === el;
                        return (
                            <Button
                                key={el}
                                {...tabButtonSx(isActive)}
                                onClick={() => handleTypeChange(el)}
                                leftIcon={<ChakraIcon as={INVOICES_LIST_TAB_ICONS[el]} boxSize={3.5} />}
                            >
                                {el[0].toUpperCase() + el.slice(1)}
                            </Button>
                        );
                    })}
                    <Box w="1px" h="20px" bg="gray.300" mx={1} flexShrink={0} />
                    <Button
                        {...tabButtonSx(!showNegotiation && showArchived)}
                        onClick={handleToggleArchived}
                        leftIcon={<ChakraIcon as={INVOICES_LIST_TAB_ICONS.archived} boxSize={3.5} />}
                    >
                        Archived
                    </Button>
                    <Button
                        {...tabButtonSx(showNegotiation)}
                        onClick={handleToggleNegotiation}
                        leftIcon={<Icon name="message" size="sm" />}
                    >
                        <HStack spacing={1.5}>
                            <Box as="span">Negotiation</Box>
                            {unreadCount > 0 && (
                                <Badge
                                    colorScheme="red"
                                    borderRadius="full"
                                    fontSize="10px"
                                    minW="18px"
                                    textAlign="center"
                                    lineHeight="18px"
                                >
                                    {unreadCount > 99 ? '99+' : unreadCount}
                                </Badge>
                            )}
                        </HStack>
                    </Button>
                    {isInvoicingDemo && (
                        <Tag size="sm" colorScheme="blue" variant="subtle" borderRadius="md" fontWeight="medium" flexShrink={0}>
                            invoicing demo mode
                        </Tag>
                    )}
                    <Tooltip label="How invoice upload, flags, and Nexsyis work" hasArrow placement="bottom">
                        <IconButton
                            aria-label="Open Parts Invoicing user guide"
                            icon={<InfoOutlineIcon />}
                            variant="ghost"
                            size="sm"
                            color="gray.600"
                            _hover={{ color: 'gray.800', bg: 'gray.100' }}
                            onClick={onPartsInvoicingGuideOpen}
                        />
                    </Tooltip>
            </ListTabRow>

            {showNegotiation ? (
                <Box flex={1} minH={0} display="flex" flexDirection="column">
                    <NegotiationFeedView
                        selectedNegotiationId={selectedNegotiationId}
                        onSelectNegotiation={handleSelectNegotiation}
                    />
                </Box>
            ) : (
                <>
                    <Flex
                        ref={toolbarRef}
                        align="center"
                        my={3}
                        px={6}
                        flexShrink={0}
                        gap={2}
                    >
                        <CollapsibleSearch
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search by RO# or Invoice#..."
                            isCompact={searchCompact}
                            width={LIST_PAGE_SEARCH_WIDTH}
                        />
                        <InvoiceAdvancedFilters
                            filters={additionalFilters}
                            onFiltersChange={handleFiltersChange}
                            locationIds={locationIds}
                            onClearLocations={() => {
                                handleLocationChange('all');
                                try {
                                    localStorage.setItem(INVOICES_LOCATION_IDS_STORAGE_KEY, 'all');
                                } catch {
                                    // ignore
                                }
                            }}
                            feedType={type}
                            archivedFilter={showArchived ? 'archived' : 'active'}
                            nexReconciliationIdentifierMode={nexReconciliationIdentifierMode}
                        />
                        <Box ml="auto" flexShrink={0}>
                            <LocationSelector
                                storageKey="location_ids"
                                selectedLocations={locationIds}
                                onLocationChange={handleLocationChange}
                                width="260px"
                                icon={TbBuilding}
                                iconOnly={locationCompact}
                            />
                        </Box>
                    </Flex>

                    <InvoicesListTable page={page} />
                </>
            )}
            <InvoicesListModals page={page} />
            <AllInvoiceActivityLogDrawer isOpen={isActivityLogOpen} onClose={onActivityLogClose} />
        </Box>
    );
}
